import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  HStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  IconButton,
  Text,
  VStack,
  InputGroup,
  InputLeftElement,
  TableContainer,
  chakra,
  Center,
  Spinner,
  useOutsideClick,
  Avatar,
} from '@chakra-ui/react';
import { Search2Icon, AddIcon, DeleteIcon, CloseIcon } from '@chakra-ui/icons';
import { fetchAdmins } from '../ProjectBoardServices/BoardModalAPI/RightModalAPI/InfoTabAPI/AssigneeAPI';

// Import the service functions
import {
  fetchAllGroups,
  createGroup,
  deleteGroup,
} from '../ProjectBoardServices/AddGroupServices/AddGroupAPIs'; // ← Adjust this path according to your folder structure

const GroupIndex = () => {
  const history = useHistory();
  const [groupName, setGroupName] = useState('');
  const [entries, setEntries] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const toast = useToast();
  const dropdownRef = useRef();

  useOutsideClick({
    ref: dropdownRef,
    handler: () => {
      setIsDropdownOpen(false);
      setSearchTerm('');
    },
  });

  // Theme values
  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");

  // Load admins (unchanged)
  const loadAdmins = async () => {
    setIsLoadingAdmins(true);
    try {
      const response = await fetchAdmins(0);
      if (response?.data && response.data.admins) {
        setAdmins(response.data.admins);
      } else {
        setAdmins([]);
      }
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        status: 'error',
        duration: 4000,
      });
      setAdmins([]);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  // Filter admins for dropdown (unchanged)
  const filteredMembers = admins.filter(admin => {
    const fullName = `${admin.admin_firstname || ''} ${admin.admin_lastname || ''}`.toLowerCase().trim();
    const email = admin.admin_email?.toLowerCase() || '';
    const search = searchTerm.toLowerCase().trim();
    return fullName.includes(search) || email.includes(search);
  });

  // Fetch all groups using service
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const data = await fetchAllGroups();

      if (data.status && Array.isArray(data.data)) {
        setEntries(data.data);
        toast({
          title: 'Groups loaded!',
          description: `${data.data.length} groups found`,
          status: 'success',
          duration: 2000,
        });
      } else {
        setEntries([]);
      }
    } catch (error) {
      console.error('Fetch groups error:', error);
      toast({
        title: 'Network Error',
        description: 'Failed to fetch groups',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Create new group using service
  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Group name and at least one member required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      const data = await createGroup(groupName, selectedMembers);

      if (data.status) {
        toast({
          title: 'Success!',
          description: `Group "${groupName}" created`,
          status: 'success',
          duration: 3000,
        });
        setGroupName('');
        setSelectedMembers([]);
        fetchGroups();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to create group',
          status: 'error',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Create group error:', error);
      toast({
        title: 'Network Error',
        description: 'Failed to create group',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // Delete group using service
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm(`Delete group ID ${groupId}?`)) return;

    try {
      const data = await deleteGroup(groupId);

      if (data.status) {
        toast({ title: 'Group deleted', status: 'success', duration: 2000 });
        fetchGroups();
      } else {
        toast({
          title: 'Delete failed',
          description: data.message || 'Unknown error',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Network Error',
        description: 'Failed to delete group',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Navigate to group view (unchanged)
  const handleRowClick = (groupCode) => {
    history.push(`/home/ap/add-group-view/${groupCode}`);
  };

  useEffect(() => {
    fetchGroups();
    loadAdmins();
  }, []);

  const toggleMember = (adminId) => {
    setSelectedMembers(prev =>
      prev.includes(adminId)
        ? prev.filter(id => id !== adminId)
        : [...prev, adminId]
    );
  };

  const removeSelectedMember = (adminId) => {
    setSelectedMembers(prev => prev.filter(id => id !== adminId));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSelectedAdminNames = () => {
    return admins
      .filter(admin => selectedMembers.includes(admin.admin_id))
      .map(admin => `${admin.admin_firstname} ${admin.admin_lastname}`);
  };

  return (
    <Box p={6} shadow="xl" bg={tableBg} borderRadius="xl">
      {/* Form Section */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
        <HStack spacing={4} align="start">
          <FormControl flex={1}>
            <FormLabel fontSize="sm" mb={1}>Group Name</FormLabel>
            <InputGroup>
              <InputLeftElement><AddIcon color="blue.400" /></InputLeftElement>
              <Input
                placeholder="e.g. 2D Services"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                size="md"
                bg="white"
              />
            </InputGroup>
          </FormControl>

          <FormControl flex={1} ref={dropdownRef}>
            <FormLabel fontSize="sm" mb={1}>Members</FormLabel>
            <Box position="relative">
              <HStack
                p={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                cursor="pointer"
                bg="white"
                _hover={{ borderColor: 'blue.300' }}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                w="full"
              >
                <Text fontSize="sm" color={selectedMembers.length > 0 ? "gray.800" : "gray.500"}>
                  {selectedMembers.length > 0 ? `${selectedMembers.length} selected` : 'Choose members'}
                </Text>
              </HStack>

              {isDropdownOpen && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  zIndex={20}
                  mt={1}
                  p={3}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  boxShadow="lg"
                  maxH="250px"
                  overflowY="auto"
                >
                  <InputGroup mb={3} size="sm">
                    <InputLeftElement><Search2Icon /></InputLeftElement>
                    <Input
                      placeholder="Search members..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="sm"
                    />
                  </InputGroup>

                  {isLoadingAdmins ? (
                    <Center py={4}>
                      <Spinner size="sm" />
                    </Center>
                  ) : (
                    <VStack align="start" spacing={1}>
                      {filteredMembers.map((admin) => (
                        <HStack
                          key={admin.admin_id}
                          w="full"
                          p={2}
                          borderRadius="md"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => toggleMember(admin.admin_id)}
                          cursor="pointer"
                        >
                          <Avatar
                            size="xs"
                            name={`${admin.admin_firstname} ${admin.admin_lastname}`}
                          />
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="13px" fontWeight="500">
                              {admin.admin_firstname} {admin.admin_lastname}
                            </Text>
                            <Text fontSize="11px" color="gray.500">
                              {admin.admin_email}
                            </Text>
                          </VStack>
                          {selectedMembers.includes(admin.admin_id) && (
                            <Text color="blue.500" fontWeight="bold">✓</Text>
                          )}
                        </HStack>
                      ))}
                      {filteredMembers.length === 0 && searchTerm && (
                        <Text fontSize="sm" color="gray.500" p={2}>
                          No members found
                        </Text>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
            </Box>

            {selectedMembers.length > 0 && (
              <HStack mt={2} spacing={1} wrap="wrap">
                {getSelectedAdminNames().map((name, idx) => (
                  <Badge
                    key={idx}
                    colorScheme="blue"
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => removeSelectedMember(selectedMembers[idx])}
                    rightIcon={<CloseIcon boxSize={2} />}
                  >
                    {name}
                  </Badge>
                ))}
              </HStack>
            )}
          </FormControl>

          <VStack spacing={2} pt={6}>
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              onClick={handleCreateGroup}
              isLoading={submitLoading}
              isDisabled={!groupName.trim() || selectedMembers.length === 0 || submitLoading}
              w="full"
            >
              Create Group
            </Button>
            <Button
              colorScheme="gray"
              onClick={fetchGroups}
              isLoading={loading}
              size="sm"
              w="full"
            >
              🔄 Refresh
            </Button>
          </VStack>
        </HStack>
      </Box>

      {/* Table */}
      <TableContainer border="1px solid" borderColor="gray.200" borderRadius="md" overflowY="auto" maxH="400px">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th color={headerColor} py={3}>ID</Th>
              <Th color={headerColor} py={3}>Name</Th>
              <Th color={headerColor} py={3}>Code</Th>
              <Th color={headerColor} py={3}>Members</Th>
              <Th color={headerColor} py={3}>Created</Th>
              <Th color={headerColor} py={3}>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr><Td colSpan={6}><Center py={8}><Spinner /></Center></Td></Tr>
            ) : entries.length === 0 ? (
              <Tr><Td colSpan={6}><Center py={8}><Text color="gray.500">No groups found</Text></Center></Td></Tr>
            ) : (
              entries.map((entry) => (
                <Tr
                  key={entry.id}
                  _hover={{ bg: rowHoverBg, cursor: 'pointer' }}
                  onClick={() => handleRowClick(entry.group_code)}
                >
                  <Td fontWeight="600" py={3}>{entry.id}</Td>
                  <Td fontWeight="600" py={3}>{entry.name}</Td>
                  <Td py={3}>
                    <Badge colorScheme="purple" fontSize="xs">
                      {entry.group_code}
                    </Badge>
                  </Td>
                  <Td py={3}>{entry.members ? Object.keys(entry.members).length : 0}</Td>
                  <Td py={3}>
                    <chakra.span fontSize="xs" color="gray.600">
                      {formatDateTime(entry.created_at)}
                    </chakra.span>
                  </Td>
                  <Td py={3}>
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGroup(entry.id);
                      }}
                      aria-label="Delete group"
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GroupIndex;